import { useCallback, useEffect } from 'react'
import { timetableImportV2Api } from '@/services/api/timetableImportV2'

export const BULK_IMPORT_BATCHES_KEY = 'bulk_import_active_batches'
export const BULK_IMPORT_BATCHES_EVENT = 'bulk-import-batches'
const STORAGE_KEY = BULK_IMPORT_BATCHES_KEY
const BATCHES_EVENT = BULK_IMPORT_BATCHES_EVENT

function notifyBatchesChanged() {
  window.dispatchEvent(new Event(BATCHES_EVENT))
}

export function clearPersistedBatches() {
  try {
    localStorage.removeItem(STORAGE_KEY)
    notifyBatchesChanged()
  } catch {
    /* ignore */
  }
}
const STALE_THRESHOLD_HOURS = 24

export interface PersistedBatchState {
  batchId: string
  jobId: string | null
  fileId: string
  fileName: string
  startedAt: string
  lastUpdated: string
  status: 'extracting' | 'resolving' | 'awaiting_resolution' | 'awaiting_import' | 'importing' | 'completed' | 'partially_failed' | 'pending_bulk_shop' | 'pending_assignment' | 'failed' | 'cancelled' | 'incomplete' | 'pending'
  totalSections: number
  resolvedSections: number
  unresolvedSessionId: string | null
}

export function usePersistedBatch() {
  const getActiveBatches = useCallback((): PersistedBatchState[] => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (!stored) return []
      const batches: PersistedBatchState[] = JSON.parse(stored)
      const threshold = Date.now() - STALE_THRESHOLD_HOURS * 60 * 60 * 1000
      return batches.filter(b => new Date(b.lastUpdated).getTime() > threshold)
    } catch {
      return []
    }
  }, [])

  const addBatch = useCallback((state: PersistedBatchState) => {
    const batches = getActiveBatches()
    const filtered = batches.filter(b => b.fileId !== state.fileId && b.batchId !== state.batchId)
    filtered.push(state)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered))
    notifyBatchesChanged()
  }, [getActiveBatches])

  const updateBatch = useCallback((batchId: string, updates: Partial<PersistedBatchState>) => {
    const batches = getActiveBatches()
    const index = batches.findIndex(b => b.batchId === batchId)
    if (index === -1) return
    const existing = batches[index]
    if (!existing) return
    batches[index] = { ...existing, ...updates, lastUpdated: new Date().toISOString() }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(batches))
  }, [getActiveBatches])

  const removeBatch = useCallback((batchId: string) => {
    const batches = getActiveBatches()
    const filtered = batches.filter(b => b.batchId !== batchId)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered))
    notifyBatchesChanged()
  }, [getActiveBatches])

  const syncWithBackend = useCallback(async (batchId: string): Promise<PersistedBatchState | null> => {
    const local = getActiveBatches().find(b => b.batchId === batchId) ?? null
    try {
      const status = await timetableImportV2Api.getBatchStatus(batchId)
      if (!local) return null
      
      const backendStatus = status.status
      let mappedStatus: PersistedBatchState['status'] = 'extracting'
      
      if (backendStatus === 'resolving') {
        mappedStatus = 'resolving'
      } else if (backendStatus === 'pending' || backendStatus === 'extracting') {
        mappedStatus = 'extracting'
      } else if (backendStatus === 'completed') {
        mappedStatus = 'completed'
      } else if (backendStatus === 'partially_failed') {
        mappedStatus = 'partially_failed'
      } else if (backendStatus === 'awaiting_import' || backendStatus === 'resolved') {
        mappedStatus = 'awaiting_import'
      } else if (backendStatus === 'pending_bulk_shop') {
        mappedStatus = 'pending_bulk_shop'
      } else if (backendStatus === 'pending_assignment') {
        mappedStatus = 'pending_assignment'
      } else {
        mappedStatus = backendStatus as PersistedBatchState['status']
      }
      
      const updated: PersistedBatchState = {
        ...local,
        status: mappedStatus,
        totalSections: status.total_sections,
        resolvedSections: status.resolved_sections,
        lastUpdated: new Date().toISOString(),
      }
      updateBatch(batchId, {
        status: mappedStatus,
        totalSections: status.total_sections,
        resolvedSections: status.resolved_sections,
      })
      return updated
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 404) {
        removeBatch(batchId)
        return null
      }
      return local
    }
  }, [getActiveBatches, updateBatch, removeBatch])

  useEffect(() => {
    const batches = getActiveBatches()
    const threshold = Date.now() - STALE_THRESHOLD_HOURS * 60 * 60 * 1000
    const stale = batches.filter(b => new Date(b.lastUpdated).getTime() < threshold)
    stale.forEach(b => removeBatch(b.batchId))
  }, [getActiveBatches, removeBatch])

  return {
    getActiveBatches,
    addBatch,
    updateBatch,
    removeBatch,
    syncWithBackend,
  }
}